import React, { Suspense, lazy, memo, useCallback, useEffect } from 'react'
import { Redirect, Route, Switch, useLocation } from 'react-router-dom'
import settings from '@nobia/zeus-components/lib/settings'
import features from '@nobia/zeus-components/lib/features'
import styled from 'styled-components'
import LoadingIndicator from '@nobia/zeus-components/lib/loadingIndicator'
import {
  trackPageView,
  setDimension,
} from '@nobia/zeus-components/lib/analytics'
import useAuth from '@nobia/zeus-components/lib/auth/useAuth'
import { AccountProvider } from '../contexts/AccountProvider'
import { MagnetCustomerListingStoreBridgeProvider } from '../contexts/MagnetCustomerListingStoreBridge'
import { DesignerViewProvider } from '../contexts/DesignerViewProvider'
import ProtectedRoute from '../components/route/ProtectedRoute'
import { useRoutes } from '../routes'
import { CloudinaryProvider } from '../components/cloudinary/CloudinaryProvider'
import { LoadingOverlay } from '../components/customers/CustomerFilter'
import { Connection } from './signalr'
import ToastContainer from '../components/toast'
import AppChatNotifications from './AppChatNotifications'
import { canonicalRoutePath } from '../utils/canonicalRoutePath'

const CustomersPage = memo(lazy(() => import('../pages/customers')))
const AccountPage = memo(lazy(() => import('../pages/account')))
const SapPage = memo(lazy(() => import('../pages/sap')))
const NotFoundPage = memo(lazy(() => import('../pages/not-found')))
const ReleaseNotesPage = memo(lazy(() => import('../pages/release-notes')))
const AppointmentsPage = memo(lazy(() => import('../pages/appointments')))
const PrepformExportPage = memo(lazy(() => import('../pages/prepform-export')))
const CustomerExportPage = memo(lazy(() => import('../pages/customer-export')))
const AdminSettingsPage = memo(lazy(() => import('../pages/admin-settings')))
const CustomerMessagesPage = memo(lazy(() => import('../pages/messages')))
const DashboardOverviewPage = memo(lazy(() => import('../pages/dashboard-overview')))

const Wrapper = styled.div`
  width: 100%;
  min-height: 100vh;
  // padding-bottom: 60px;
  background: ${(p) => p.theme.base.background};
  overflow-x: hidden;
  font-size: ${(p) => p.theme.typography.paragraph.fontSize};
`

const LoadingWrapper = styled.div`
  width: 100%;
  height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
`

// eslint-disable-next-line react/prop-types
const Loading = ({ children }) => (
  <Suspense
    fallback={
      <LoadingWrapper>
        <LoadingOverlay>
          <LoadingIndicator size="large" />
        </LoadingOverlay>
      </LoadingWrapper>
    }
  >
    {children}
  </Suspense>
)

export const App = () => {
  const { status, profile } = useAuth()
  const location = useLocation()
  const {
    customers,
    account,
    sap,
    releaseNotes,
    appointments,
    prepformExport,
    customerExport,
    adminSettings,
    customerMessages,
    dashboard,
  } = useRoutes()

  const setAnalyticsDimension = useCallback(() => {
    if (profile && profile.store_id) {
      setDimension('dimension12', profile.store_id)
    }
  }, [profile])

  useEffect(() => {
    setAnalyticsDimension()
    trackPageView(`${settings.basename}${location.pathname}`)
  }, [location])

  useEffect(() => {
    if (status === 'authenticated' && profile) {
      Connection.start().catch(err => console.error('SignalR start failed:', err))
    }
  }, [status, profile])

  return (
    <Wrapper>
      <ToastContainer
        position="top-right"
        autoClose={3000}
        theme="colored"
      />
      <CloudinaryProvider>
        <AccountProvider>
          <AppChatNotifications />
          <MagnetCustomerListingStoreBridgeProvider>
          <DesignerViewProvider>
          <Switch>
            <ProtectedRoute
              role="staff"
              path="/"
              render={() => (
                <Redirect
                  to={
                    settings.brand === 'magnet'
                      ? canonicalRoutePath(dashboard.path)
                      : canonicalRoutePath(customers.path)
                  }
                />
              )}
              exact
            />
            <ProtectedRoute
              role="staff"
              path={customers.path}
              render={() => (
                <Loading>
                  <CustomersPage />
                </Loading>
              )}
            />
            <ProtectedRoute
              role="staff"
              path={dashboard.path}
              exact
              render={() => (
                <Loading>
                  {settings.brand === 'magnet' ? (
                    <DashboardOverviewPage />
                  ) : (
                    <Redirect to={canonicalRoutePath(customers.path)} />
                  )}
                </Loading>
              )}
            />
            <ProtectedRoute
              role="staff"
              path={account.path}
              render={() => (
                <Loading>
                  <AccountPage />
                </Loading>
              )}
            />
            {features.sap === true && (
              <ProtectedRoute
                role="sapadmin"
                path={sap.path}
                render={() => (
                  <Loading>
                    <SapPage />
                  </Loading>
                )}
              />
            )}
            <ProtectedRoute
              role="staff"
              path={appointments.path}
              render={() => (
                <Loading>
                  <AppointmentsPage />
                </Loading>
              )}
            />
            <ProtectedRoute
              role="staff"
              path={releaseNotes.path}
              render={() => (
                <Loading>
                  <ReleaseNotesPage />
                </Loading>
              )}
            />
            <ProtectedRoute
              role="staff"
              path={customerMessages.path}
              render={() => (
                <Loading>
                  <CustomerMessagesPage />
                </Loading>
              )}
            />
            <ProtectedRoute
              role="NOBG-SalesTool-ExportPrepform"
              path={prepformExport.path}
              render={() => (
                <Loading>
                  <PrepformExportPage />
                </Loading>
              )}
            />
            <ProtectedRoute
              role="staff"
              path={customerExport.path}
              render={() => (
                <Loading>
                  <CustomerExportPage />
                </Loading>
              )}
            />
            <ProtectedRoute
              role="GroupAdmin"
              path={adminSettings.path}
              render={() => (
                <Loading>
                  <AdminSettingsPage />
                </Loading>
              )}
            />
            <Route
              path="/sso"
              exact
              render={() => {
                window.location.href = '/login?provider=azure&path=/sales-tool'
                return <></>
              }}
            />
            <ProtectedRoute
              role="staff"
              render={() => (
                <Loading>
                  <NotFoundPage />
                </Loading>
              )}
            />
          </Switch>
          </DesignerViewProvider>
          </MagnetCustomerListingStoreBridgeProvider>
        </AccountProvider>
      </CloudinaryProvider>
    </Wrapper>
  )
}

export default App
